/*
* Copyright © 2019 Insticator, Inc. All Rights Reserved.
*
* CONFIDENTIALITY NOTICE: THIS SOFTWARE CONTAINS CONFIDENTIAL INFORMATION OF
* INSTICATOR, INC. USE, DISCLOSURE OR REPRODUCTION IS PROHIBITED WITHOUT THE PRIOR
* EXPRESS WRITTEN PERMISSION OF INSTICATOR, INC.
*/



/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
import { settings } from 'config/config';
import { device, integration, pageurl, hostname } from 'utils/environment'; // this checks our server, device, the way we're embedded in a client site, etc


// ----- DATA TRANSPILERS ----- //
const uniqueEventDataTranspiler = (eventName, target, additionalProps) => {
  if (eventName === 'ceuContentEngaged' && target === 'skip') {
    return additionalProps ? additionalProps : {};
  }
  if (eventName === 'ceuLoad' && target === 'ceuLoad') {
    if (!additionalProps) return {}
    return {
      is_in_view: additionalProps.embed.isInview,
      in_view_setting: settings.embed.inView,
      amount_of_time_in_view: additionalProps.embed.amountOfTimeInView
    };
  }
  if (eventName === 'ceuClicks' && (target === 'insticatorLogo' || target === 'partnerLogo')) {
    return {
      url: additionalProps.logo.url,
      type: additionalProps.logo.type
    };
  }
  if (eventName === 'ceuContentLoad' && (target === 'quiz' || target === 'poll')) {
    return undefined
  }
  if (eventName === 'ceuContentLoad' && target === 'story') {
    return undefined
  }
  if (eventName === 'ceuContentEngaged' && (target === 'quiz' || target === 'poll')) {
    if (additionalProps.skip) return additionalProps
    return {
      answer: {
        id: additionalProps.answer.id,
        type: additionalProps.question.type,
        correct_answer: additionalProps.isCorrect
      },
      time_before_answer: additionalProps.amountOfTimeBeforeAnswering,
      result_wait_time: additionalProps.resultWaitTime,
      valid_request: additionalProps.validRequest
    }
  }
  if (eventName === 'ceuContentEngaged' && target === 'story') {
    return additionalProps
  }
  if (eventName === 'ceuClicks' && target === 'logo') {
    delete additionalProps.logo.frame
    return {...additionalProps.logo}
  }
  return additionalProps
};

// we can use this util to trim frameData
const createContentDetails = ({ props, additionalProps }) => {
  const getFrameData = () => {
    if (props && props.frameData) {
      if (props.frameData.frameData) return props.frameData.frameData // this is the case for name: ceuContentEngaged, target: story 
      return props.frameData
    }
    if (additionalProps && additionalProps.frameData) {
      return additionalProps.frameData
    }
    return null
  }
  const data = getFrameData()
  let newData = JSON.parse(JSON.stringify(data));

  if (!newData) return null

  delete newData.frameStatus;

  delete newData.answers;
  delete newData.question;
  delete newData.story;
  delete newData.questionSignature;
  delete newData.answerSignature;

  if (data.answers) newData.answerIds = data.answers.map(answer => answer.id);
  if (data.question) {
    newData.question = {
      id: data.question.id,
      type: data.question.type,
      text: data.question.text
    }
  }
  if (data.story) {
    newData.story = {
      id: data.story.id,
      text: data.story.text,
      ctaText: data.story.ctaText
    }
  }
  return newData;
};

const getEventTarget = ({ name, target, props }) => {
  if (!!target) return target
  if (name === 'ceuContentEngaged' || name === 'ceuContentLoad') {
    if (props && props.frameType) return props.frameType
    if (props && props.frameData && props.frameData.type) return props.frameData.type
  }
  return target
}

const getQueuePosition = ({ props }) => {
  if (props.currentContentStatus) return props.currentContentStatus.frame 
  if (props.frame) return props.frame
  return null
}

const getEmbedContext = () => ({
  embed: {
    version: settings.embed.version,
    id: settings.embed.uuid,
    name: settings.embed.name,
    location: settings.embed.location,
    style: {
      height: settings.embed.styles[device].height,
      width: settings.embed.styles[device].width
    }
  },
  environment: {
    device: device,
    integration_type: integration // this can sometimes be innaccurate though we are working to improve it
  },
  site: {
    id: settings.site.uuid,
    url: settings.site.url,
    page_url: pageurl,
    hostname: hostname
  },
  account: {
    id: settings.client.uuid
  }
})

// <<<<<<<<<<<<<<<<<<<<<< ---------------------- DATA TO SEND TO FKP ---------------------- >>>>>>>>>>>>>>>>>>>>> //
const createEventData = ({ type, name, target: gotTarget, props, additionalProps }) => {
  const target = getEventTarget({ name, target: gotTarget, props })
  
  return {
    timestamp: new Date().toISOString(), //'2019-02-12T15:41:26.839Z',
    embed_context: getEmbedContext(),
    event_context: props === null || props === undefined ? null : {
      content: {
        type: target,
        queue_position: getQueuePosition({ props })
      },
      contentDetails: createContentDetails({ props, additionalProps })
    },
    event_data: {
      type: type,
      name: name,
      target: target || undefined,
      data: uniqueEventDataTranspiler(name, target, additionalProps)
    }
  }
};

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export { createEventData };
